import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import { apiFetch } from '../lib/api';
import { Gift, Plus, X, Loader2, Edit2, Save, Power, Trash2 } from 'lucide-react';
import type { GiftCode } from '../types';

function formatCents(cents = 0): string {
  return `HK$${Math.round(cents / 100).toLocaleString('zh-HK')}`;
}

function toDateInput(ts?: number): string {
  if (!ts) return '';
  const d = new Date(ts * 1000);
  return isNaN(d.getTime()) ? '' : d.toISOString().slice(0, 10);
}

function fromDateInput(value: string, endOfDay = false): number {
  if (!value) return 0;
  const d = new Date(value);
  if (endOfDay) {
    d.setHours(23, 59, 59, 999);
  } else {
    d.setHours(0, 0, 0, 0);
  }
  return Math.floor(d.getTime() / 1000);
}

const TYPE_LABELS: Record<string, string> = {
  percentage: '百分比折扣',
  fixed_amount: '固定金額',
  free_shipping: '免運費',
};

const emptyForm = {
  code: '',
  type: 'fixed_amount' as GiftCode['type'],
  value: 0,
  minOrderAmount: 0,
  maxUses: '',
  validFrom: toDateInput(Math.floor(Date.now() / 1000)),
  validUntil: '',
  isActive: true,
  description: '',
};

const AdminPromoCodes: React.FC = () => {
  const navigate = useNavigate();
  const [codes, setCodes] = useState<GiftCode[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchCodes();
  }, [navigate]);

  const fetchCodes = async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiFetch('/api/admin/gift-codes');
      if (!res.ok) throw new Error('獲取優惠碼失敗');
      const json = await res.json();
      const codes = json.data?.giftCodes || json.giftCodes || [];
      setCodes(codes);
    } catch (e: any) {
      setError(e.message || '加載失敗');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    if (confirm('確定要登出嗎？')) {
      localStorage.removeItem('admin_token');
      localStorage.removeItem('admin_user');
      navigate('/admin');
    }
  };

  const resetForm = () => {
    setForm(emptyForm);
    setEditingId(null);
  };

  const openCreate = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (code: GiftCode) => {
    setEditingId(code.id);
    setForm({
      code: code.code,
      type: code.type,
      value: code.type === 'fixed_amount' ? (code.value || 0) / 100 : (code.value || 0),
      minOrderAmount: (code.minOrderAmount || 0) / 100,
      maxUses: code.maxUses != null ? String(code.maxUses) : '',
      validFrom: toDateInput(code.validFrom),
      validUntil: toDateInput(code.validUntil),
      isActive: code.isActive === 1,
      description: code.description || '',
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    setSuccess(null);

    const rawValue = Number(form.value) || 0;
    const rawMin = Number(form.minOrderAmount) || 0;
    const payload: Record<string, unknown> = {
      type: form.type,
      value: form.type === 'fixed_amount' ? Math.round(rawValue * 100) : rawValue,
      minOrderAmount: Math.round(rawMin * 100),
      maxUses: form.maxUses ? Number(form.maxUses) : null,
      validFrom: fromDateInput(form.validFrom),
      validUntil: form.validUntil ? fromDateInput(form.validUntil, true) : null,
      isActive: form.isActive,
      description: form.description.trim() || null,
    };

    try {
      let res;
      if (editingId) {
        res = await apiFetch(`/api/admin/gift-codes/${editingId}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      } else {
        payload.code = form.code.trim().toUpperCase();
        res = await apiFetch('/api/admin/gift-codes', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data.detail || data.error || '儲存失敗');
      setSuccess(editingId ? '優惠碼已更新' : '優惠碼已新增');
      closeForm();
      fetchCodes();
    } catch (e: any) {
      setError(e.message || '儲存失敗');
    } finally {
      setSubmitting(false);
      setTimeout(() => { setSuccess(null); setError(null); }, 4000);
    }
  };

  const handleDelete = async (code: GiftCode) => {
    if (!confirm(`確定要刪除優惠碼「${code.code}」嗎？此操作無法復原。`)) return;
    try {
      const res = await apiFetch(`/api/admin/gift-codes/${code.id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('刪除失敗');
      setSuccess('優惠碼已刪除');
      fetchCodes();
    } catch (e: any) {
      setError(e.message || '刪除失敗');
      setTimeout(() => setError(null), 3000);
    }
  };

  const toggleActive = async (code: GiftCode) => {
    try {
      const res = await apiFetch(`/api/admin/gift-codes/${code.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ isActive: code.isActive !== 1 }),
      });
      if (!res.ok) throw new Error('更新狀態失敗');
      fetchCodes();
    } catch (e: any) {
      setError(e.message || '更新狀態失敗');
      setTimeout(() => setError(null), 3000);
    }
  };

  return (
    <AdminLayout currentPage="promo-codes" onLogout={handleLogout}>
      <div className="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800 dark:text-white flex items-center gap-2">
            <Gift className="w-6 h-6 text-red-600" />
            優惠碼管理
          </h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">新增與管理折扣碼，設定使用次數上限</p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-1 bg-red-600 text-white px-4 py-2.5 rounded-lg text-sm hover:bg-red-700 transition-colors"
        >
          <Plus className="w-4 h-4" /> 新增優惠碼
        </button>
      </div>

      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 flex justify-between items-center dark:bg-red-900/20 dark:border-red-800 dark:text-red-300">
          <span>{error}</span>
          <button onClick={() => setError(null)} className="text-red-700 hover:text-red-900 dark:text-red-300 dark:hover:text-red-100"><X className="w-5 h-5" /></button>
        </div>
      )}
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg text-green-700 flex justify-between items-center dark:bg-green-900/20 dark:border-green-800 dark:text-green-300">
          <span>{success}</span>
          <button onClick={() => setSuccess(null)} className="text-green-700 hover:text-green-900 dark:text-green-300 dark:hover:text-green-100"><X className="w-5 h-5" /></button>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-gray-100 dark:bg-gray-900 text-left text-xs font-semibold text-gray-600 dark:text-gray-400 uppercase tracking-wider">
              <tr>
                <th className="p-4">優惠碼</th>
                <th className="p-4">類型</th>
                <th className="p-4">折扣值</th>
                <th className="p-4">最低消費</th>
                <th className="p-4">使用次數</th>
                <th className="p-4">有效期</th>
                <th className="p-4">狀態</th>
                <th className="p-4">描述</th>
                <th className="p-4">操作</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading ? (
                <tr><td colSpan={9} className="p-8 text-center text-gray-400">加載中...</td></tr>
              ) : codes.length === 0 ? (
                <tr><td colSpan={9} className="p-8 text-center text-gray-500 dark:text-gray-400">暫無優惠碼</td></tr>
              ) : (
                codes.map((code) => (
                  <tr key={code.id} className="hover:bg-red-50 dark:hover:bg-gray-700 transition-colors">
                    <td className="p-4 font-mono font-bold text-gray-800 dark:text-white">{code.code}</td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">{TYPE_LABELS[code.type] || code.type}</td>
                    <td className="p-4 text-gray-800 dark:text-white">
                      {code.type === 'percentage' ? `${code.value}%` : code.type === 'fixed_amount' ? formatCents(code.value) : '-'}
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">{formatCents(code.minOrderAmount)}</td>
                    <td className="p-4 text-gray-600 dark:text-gray-300">
                      {code.usedCount || 0}
                      {code.maxUses != null ? ` / ${code.maxUses}` : ' / 不限'}
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-300 whitespace-nowrap">
                      {toDateInput(code.validFrom)}
                      {code.validUntil ? ` ~ ${toDateInput(code.validUntil)}` : ' 起'}
                    </td>
                    <td className="p-4">
                      <button
                        onClick={() => toggleActive(code)}
                        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium transition ${
                          code.isActive === 1
                            ? 'bg-green-100 text-green-700 border border-green-200'
                            : 'bg-gray-100 text-gray-700 border border-gray-200'
                        }`}
                      >
                        <Power size={12} />
                        {code.isActive === 1 ? '啟用中' : '已停用'}
                      </button>
                    </td>
                    <td className="p-4 text-gray-600 dark:text-gray-300 max-w-xs truncate">{code.description || '-'}</td>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => openEdit(code)}
                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 text-xs font-medium"
                        >
                          <Edit2 size={12} /> 編輯
                        </button>
                        <button
                          onClick={() => handleDelete(code)}
                          className="inline-flex items-center gap-1 text-red-600 hover:text-red-800 text-xs font-medium"
                        >
                          <Trash2 size={12} /> 刪除
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                {editingId ? <Edit2 size={20} /> : <Plus size={20} />}
                {editingId ? '編輯優惠碼' : '新增優惠碼'}
              </h2>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">優惠碼</label>
                  <input
                    type="text"
                    required
                    disabled={!!editingId}
                    value={form.code}
                    onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                    placeholder="X1493OFF"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white disabled:bg-gray-100 disabled:text-gray-500"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">類型</label>
                    <select
                      value={form.type}
                      onChange={(e) => setForm({ ...form, type: e.target.value as GiftCode['type'] })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    >
                      <option value="fixed_amount">固定金額</option>
                      <option value="percentage">百分比折扣</option>
                      <option value="free_shipping">免運費</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {form.type === 'percentage' ? '折扣 %' : form.type === 'fixed_amount' ? '金額（HK$）' : '值'}
                    </label>
                    <input
                      type="number"
                      min="0"
                      required
                      value={form.value}
                      onChange={(e) => setForm({ ...form, value: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                    {form.type === 'fixed_amount' && (
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">輸入港幣金額，例如 50 = HK$50</p>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">最低消費（HK$）</label>
                    <input
                      type="number"
                      min="0"
                      value={form.minOrderAmount}
                      onChange={(e) => setForm({ ...form, minOrderAmount: Number(e.target.value) })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">使用次數上限（留空=不限）</label>
                    <input
                      type="number"
                      min="1"
                      value={form.maxUses}
                      onChange={(e) => setForm({ ...form, maxUses: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">生效日期</label>
                    <input
                      type="date"
                      required
                      value={form.validFrom}
                      onChange={(e) => setForm({ ...form, validFrom: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">到期日期（留空=永久）</label>
                    <input
                      type="date"
                      value={form.validUntil}
                      onChange={(e) => setForm({ ...form, validUntil: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">描述</label>
                  <textarea
                    rows={2}
                    value={form.description}
                    onChange={(e) => setForm({ ...form, description: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-red-500 outline-none bg-white dark:bg-gray-800 dark:text-white resize-none"
                  />
                </div>
                <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.isActive}
                    onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                    className="w-4 h-4 text-red-600 rounded focus:ring-red-500"
                  />
                  立即啟用
                </label>
                <div className="flex gap-2 pt-2">
                  <button
                    type="button"
                    onClick={closeForm}
                    className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600"
                  >
                    取消
                  </button>
                  <button
                    type="submit"
                    disabled={submitting || !form.code.trim()}
                    className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-1"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    {editingId ? '儲存' : '新增'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminPromoCodes;
